import * as React from "react";
import {
  Button,
  Divider,
  Spinner,
  Textarea,
  makeStyles,
  tokens,
} from "@fluentui/react-components";
import { postExternalConsumptionAPI } from "../../../utils/httpUtilsExternalAPI";
import { SELF_HELP_FEEDBACK_CATEGORIES } from "../interfaces/selfHelpFeedback";
import {
  SelfHelpFeedbackMessageDescriptor,
  SelfHelpFeedbackTicketContext,
  SelfHelpFeedbackValidationRequest,
  SelfHelpFeedbackValidationResponse,
} from "../interfaces/api";
import { SelfHelpFeedbackState } from "../interfaces/selfHelpFeedback";
import {
  canRevealSelfHelpTicketing,
  rehydrateSelfHelpFeedbackState,
  selfHelpFeedbackReducer,
} from "../utils/selfHelpFeedbackState";
import { buildSelfHelpFeedbackTicketContext } from "../utils/selfHelpFeedbackSession";
import { trackSelfHelpFeedbackValidatorFailure } from "../utils/selfHelpFeedbackTelemetry";

const useStyles = makeStyles({
  root: {
    display: "flex",
    flexDirection: "column",
    gap: "12px",
  },
  actions: {
    display: "flex",
    flexWrap: "wrap",
    gap: "8px",
  },
  heading: {
    fontSize: tokens.fontSizeBase400,
    fontWeight: tokens.fontWeightSemibold,
    margin: 0,
  },
  required: {
    color: tokens.colorPaletteRedForeground1,
    marginLeft: "4px",
  },
  categoryCard: {
    display: "flex",
    flexDirection: "column",
    gap: "12px",
  },
  radioGroup: {
    border: 0,
    display: "flex",
    flexDirection: "column",
    gap: "8px",
    margin: 0,
    padding: 0,
  },
  radioLabel: {
    alignItems: "center",
    display: "flex",
    gap: "8px",
  },
  feedback: {
    display: "flex",
    flexDirection: "column",
    gap: "8px",
  },
  guidance: {
    backgroundColor: tokens.colorNeutralBackground2,
    borderRadius: tokens.borderRadiusMedium,
    padding: "10px",
  },
  error: {
    color: tokens.colorPaletteRedForeground1,
  },
});

export type SelfHelpTicketReadyContext = SelfHelpFeedbackTicketContext;

export interface SelfHelpResolvedContext {
  journeyId: string;
  selfHelpTurnId: string;
}

export interface SelfHelpFeedbackMessageProps {
  descriptor: SelfHelpFeedbackMessageDescriptor;
  platformAuthClient?: any;
  validateFeedback?: (
    request: SelfHelpFeedbackValidationRequest
  ) => Promise<SelfHelpFeedbackValidationResponse>;
  initialState?: SelfHelpFeedbackState;
  initialFeedback?: string;
  ticketRequestStarted?: boolean;
  onStateChange?: (state: SelfHelpFeedbackState) => void;
  onFeedbackDraftChange?: (draft: string) => void;
  onResolved?: (context: SelfHelpResolvedContext) => void;
  onTicketReady?: (
    context: SelfHelpTicketReadyContext
  ) => boolean | Promise<boolean>;
}

const SelfHelpFeedbackMessage: React.FC<SelfHelpFeedbackMessageProps> = ({
  descriptor,
  platformAuthClient,
  validateFeedback,
  initialState,
  initialFeedback,
  ticketRequestStarted = false,
  onStateChange,
  onFeedbackDraftChange,
  onResolved,
  onTicketReady,
}) => {
  const styles = useStyles();
  const copy = descriptor.copy;
  const [state, dispatch] = React.useReducer(
    selfHelpFeedbackReducer,
    { descriptor, initialState },
    ({ descriptor: currentDescriptor, initialState: persistedState }) =>
      rehydrateSelfHelpFeedbackState(
        persistedState,
        {
          descriptorId: currentDescriptor.descriptorId,
          owningSelfHelpTurnId: currentDescriptor.owningSelfHelpTurnId,
        },
        () => currentDescriptor.journeyId
      )
  );
  const [feedback, setFeedback] = React.useState(
    initialFeedback ??
      (initialState && "feedback" in initialState ? initialState.feedback : "")
  );
  const [submitting, setSubmitting] = React.useState(false);
  const [ticketRequested, setTicketRequested] = React.useState(
    ticketRequestStarted
  );
  const submittingRef = React.useRef(false);
  const onStateChangeRef = React.useRef(onStateChange);
  const categoryHeadingRef = React.useRef<HTMLHeadingElement>(null);
  const feedbackRef = React.useRef<HTMLTextAreaElement>(null);
  const revisionRef = React.useRef<HTMLDivElement>(null);
  const ticketReadyRef = React.useRef<HTMLDivElement>(null);
  const focusedStatusRef = React.useRef<SelfHelpFeedbackState["status"]>();
  const hasFocusedRevisionRef = React.useRef(false);
  const feedbackId = `self-help-feedback-${state.journeyId}`;
  const feedbackErrorId = `${feedbackId}-error`;

  React.useEffect(() => {
    onStateChangeRef.current = onStateChange;
  }, [onStateChange]);

  React.useEffect(() => {
    onStateChangeRef.current?.(state);
  }, [state]);

  React.useEffect(() => {
    if (
      initialState?.status === "abandoned" &&
      state.status !== "abandoned"
    ) {
      dispatch({ type: "abandon" });
    }
  }, [initialState?.status, state.status]);

  React.useEffect(() => {
    if (ticketRequestStarted) {
      setTicketRequested(true);
    }
  }, [ticketRequestStarted]);

  React.useEffect(() => {
    if (focusedStatusRef.current === state.status) {
      return;
    }
    focusedStatusRef.current = state.status;

    if (state.status === "category") {
      categoryHeadingRef.current?.focus({ preventScroll: true });
    } else if (state.status === "feedback") {
      feedbackRef.current?.focus({ preventScroll: true });
    } else if (
      state.status === "revision" &&
      !hasFocusedRevisionRef.current
    ) {
      hasFocusedRevisionRef.current = true;
      revisionRef.current?.focus({ preventScroll: true });
    } else if (
      state.status === "ticket-ready" ||
      state.status === "fallback"
    ) {
      ticketReadyRef.current?.focus({ preventScroll: true });
    }
  }, [state.status]);

  const selectedCategory =
    "category" in state ? state.category : undefined;
  const showCategory =
    state.status === "category" ||
    state.status === "feedback" ||
    state.status === "validating" ||
    state.status === "revision";
  const showFeedback =
    state.status === "feedback" ||
    state.status === "validating" ||
    state.status === "revision";

  const callValidator = React.useCallback(
    async (
      request: SelfHelpFeedbackValidationRequest
    ): Promise<SelfHelpFeedbackValidationResponse> => {
      if (validateFeedback) {
        return validateFeedback(request);
      }

      const response = await postExternalConsumptionAPI(
        "api/SelfHelpFeedback/validate",
        request,
        platformAuthClient
      );
      return response?.data ?? response;
    },
    [platformAuthClient, validateFeedback]
  );

  const handleSubmit = async () => {
    if (submittingRef.current || !selectedCategory) {
      return;
    }

    dispatch({
      type: "submitFeedback",
      feedback,
      requiredError: copy.feedbackRequiredError,
    });
    if (!feedback.trim()) {
      return;
    }

    submittingRef.current = true;
    setSubmitting(true);
    const request: SelfHelpFeedbackValidationRequest = {
      journeyId: state.journeyId,
      selfHelpTurnId: descriptor.selfHelpTurnId,
      validationContextId: descriptor.validationContextId,
      appName: descriptor.appName || "mcapshelpcopilot",
      category: selectedCategory,
      feedback,
    };

    try {
      const result = await callValidator(request);
      if (result?.status === "ready") {
        dispatch({
          type: "validationPassed",
          primaryFailureReason: result.primaryFailureReason,
          guidanceDisposition: result.guidanceDisposition,
          failedStepNumber: result.failedStepNumber,
        });
      } else if (
        result?.status === "needs_improvement" &&
        typeof result.guidance === "string" &&
        result.guidance.length > 0
      ) {
        dispatch({
          type: "validationNeedsImprovement",
          guidance: result.guidance,
        });
      } else {
        trackSelfHelpFeedbackValidatorFailure({
          failureType: "server",
          validationStatus: result?.status || "invalid_response",
          correlationId: result?.correlationId,
        });
        dispatch({
          type: "validationFailed",
          message: copy.technicalFallback,
        });
      }
    } catch (error: any) {
      trackSelfHelpFeedbackValidatorFailure({
        failureType: "transport",
        httpStatus: error?.response?.status,
        correlationId: error?.response?.data?.correlationId,
      });
      dispatch({
        type: "validationFailed",
        message: copy.technicalFallback,
      });
    } finally {
      submittingRef.current = false;
      setSubmitting(false);
    }
  };

  const handleTicketReady = async () => {
    if (
      !canRevealSelfHelpTicketing(state) ||
      !selectedCategory ||
      ticketRequested ||
      (state.status !== "ticket-ready" && state.status !== "fallback")
    ) {
      return;
    }

    setTicketRequested(true);
    const accepted = await onTicketReady?.(
      buildSelfHelpFeedbackTicketContext(descriptor, state)
    );
    if (accepted === false) {
      setTicketRequested(false);
    }
  };

  const handleResolved = () => {
    dispatch({ type: "answerResolution", resolved: true });
    onResolved?.({
      journeyId: state.journeyId,
      selfHelpTurnId: descriptor.selfHelpTurnId,
    });
  };

  if (state.status === "resolved") {
    return <div>{copy.resolvedAcknowledgement}</div>;
  }

  if (state.status === "abandoned") {
    return null;
  }

  if (state.status === "ticket-ready" || state.status === "fallback") {
    return (
      <div className={styles.root}>
        <div
          ref={ticketReadyRef}
          role="status"
          aria-label={
            state.status === "ticket-ready"
              ? copy.validatedAcknowledgement
              : copy.technicalFallback
          }
          aria-live="polite"
          tabIndex={-1}
        >
          {state.status === "ticket-ready"
            ? copy.validatedAcknowledgement
            : copy.technicalFallback}
        </div>
        <Button
          appearance="primary"
          disabled={ticketRequested}
          onClick={handleTicketReady}
        >
          {descriptor.ticketActionLabel || copy.ticketActionLabel}
        </Button>
      </div>
    );
  }

  return (
    <div className={styles.root}>
      {state.status === "resolution" && (
        <>
          <h3 className={styles.heading}>
            {copy.resolutionPrompt}
            <span className={styles.required} aria-hidden="true">
              *
            </span>
          </h3>
          <div
            className={styles.actions}
            role="group"
            aria-label={copy.resolutionPrompt}
            aria-required="true"
          >
            <Button
              appearance="primary"
              onClick={handleResolved}
            >
              {copy.resolvedLabel}
            </Button>
            <Button
              onClick={() =>
                dispatch({ type: "answerResolution", resolved: false })
              }
            >
              {copy.unresolvedLabel}
            </Button>
          </div>
        </>
      )}

      {showCategory && (
        <section className={styles.categoryCard}>
          <div>
            <h3
              ref={categoryHeadingRef}
              className={styles.heading}
              tabIndex={-1}
            >
              {copy.supportRequestHeader}
            </h3>
            <div>{copy.supportRequestBody}</div>
          </div>
          <Divider />
          <div>
            <h3 className={styles.heading}>
              {copy.feedbackHeading}
              <span className={styles.required} aria-hidden="true">
                *
              </span>
            </h3>
            <div>{copy.feedbackPrompt}</div>
          </div>
          <fieldset
            className={styles.radioGroup}
            role="radiogroup"
            aria-label={copy.feedbackPrompt}
            aria-required="true"
            disabled={submitting}
          >
            {SELF_HELP_FEEDBACK_CATEGORIES.map((category) => (
              <label className={styles.radioLabel} key={category}>
                <input
                  type="radio"
                  name={`self-help-category-${state.journeyId}`}
                  value={category}
                  checked={selectedCategory === category}
                  required
                  onChange={() =>
                    dispatch({ type: "selectCategory", category })
                  }
                />
                {copy.categoryLabels[category]}
              </label>
            ))}
          </fieldset>
        </section>
      )}

      {showFeedback && (
        <div className={styles.feedback}>
          {state.status === "revision" && (
            <div
              ref={revisionRef}
              className={styles.guidance}
              role="status"
              aria-live="polite"
              tabIndex={-1}
            >
              {state.validatorGuidance}
            </div>
          )}
          <label htmlFor={feedbackId}>
            {copy.feedbackDetailPrompt}
            <span className={styles.required} aria-hidden="true">
              *
            </span>
          </label>
          <Textarea
            ref={feedbackRef}
            id={feedbackId}
            aria-label={copy.feedbackDetailPrompt}
            aria-busy={submitting}
            aria-invalid={Boolean(
              (state.status === "feedback" || state.status === "revision") &&
                state.error
            )}
            aria-describedby={
              (state.status === "feedback" || state.status === "revision") &&
              state.error
                ? feedbackErrorId
                : undefined
            }
            required
            value={feedback}
            disabled={submitting}
            onChange={(_, data) => {
              setFeedback(data.value);
              onFeedbackDraftChange?.(data.value);
            }}
          />
          {(state.status === "feedback" || state.status === "revision") &&
            state.error && (
              <div
                id={feedbackErrorId}
                className={styles.error}
                role="alert"
                aria-live="assertive"
              >
                {state.error}
              </div>
            )}
          {submitting && (
            <div role="status" aria-live="polite">
              <Spinner size="tiny" />
              {copy.validationProgress}
            </div>
          )}
          <Button
            appearance="primary"
            disabled={submitting}
            onClick={handleSubmit}
          >
            {state.status === "revision"
              ? copy.resubmitFeedbackLabel
              : copy.submitFeedbackLabel}
          </Button>
        </div>
      )}
    </div>
  );
};

export default SelfHelpFeedbackMessage;

// Generated by Copilot
// updated on: 2026-09-13 | 10 lines changed | [Auto Generated] | Reveal typed ticket handoff without legacy guidance footer | Time Taken 2 min
// updated on: 2026-09-09 | Story 1297509 TASK-005 | Added mandatory accessible self-help feedback validation gate | Time Taken 18 min
// updated on: 2026-09-09 | Story 1297509 TASK-009 | Added persisted journey restoration and typed ticket handoff context | Time Taken 10 min
// updated on: 2026-09-09 | Story 1297509 UI audit | Added stable error semantics and one-time step focus management | Time Taken 8 min
// updated on: 2026-09-10 | Story 1297509 hardening | Removed UI fallback copy and consumed the complete descriptor contract | Time Taken 4 min
// updated on: 2026-09-11 | Story 1297509 DEV fix | Passed the platform auth client to validation HTTP calls | Time Taken 3 min
// updated on: 2026-09-11 | Story 1297509 DEV fix | Prevented focus scrolling and restored request-type ticket action labels | Time Taken 3 min
// updated on: 2026-09-11 | Story 1297509 DEV fix | Restored the existing Need Guidance card after validation | Time Taken 3 min
// updated on: 2026-09-13 | 5 lines changed | [Auto Generated] | Send only opaque server-context identity and user feedback for validation | Time Taken 3 min
// updated on: 2026-09-13 | 1 line changed | [Auto Generated] | Prevent double-slash validator URL through shared API helper | Time Taken 1 min
// updated on: 2026-09-14 | 17 lines changed | [Auto Generated] | Preserve failure reason and emit privacy-safe validator diagnostics | Time Taken 4 min
